import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Mic, Video } from "lucide-react";
import { Button } from "@/components/ui/button";

export const RECORD_NOTICE = "This interview is recorded and reviewed by the hiring team";
export const NO_CAMERA = "Camera unavailable — you can continue with voice only";

export function useMicLevel(stream) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!stream || !Ctx || !stream.getAudioTracks().length) return undefined;
    const ctx = new Ctx();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Float32Array(an.fftSize);
    let raf;
    const tick = () => {
      an.getFloatTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i += 1) s += buf[i] * buf[i];
      setLevel(Math.min(1, Math.sqrt(s / buf.length) * 6));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); ctx.close().catch(() => {}); };
  }, [stream]);
  return level;
}

// Device check: camera preview + mic level. onDone(stream|null) — stream only when continuing with camera.
export function DeviceCheck({ onDone }) {
  const ref = useRef(null);
  const [stream, setStream] = useState(null);
  const [cam, setCam] = useState("checking");
  const level = useMicLevel(stream);
  useEffect(() => {
    let s = null;
    let live = true;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" }, audio: { echoCancellation: true, noiseSuppression: true } });
        if (!live) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s); setCam("ok");
      } catch {
        setCam("unavailable");
        try { s = await navigator.mediaDevices.getUserMedia({ audio: true }); if (live) setStream(s); } catch { /* typing fallback later */ }
      }
    })();
    return () => { live = false; };
  }, []);
  useEffect(() => { if (ref.current && stream && cam === "ok") ref.current.srcObject = stream; }, [stream, cam]);
  const go = (withCam) => {
    if (!withCam) stream?.getTracks().forEach((t) => t.stop());
    onDone(withCam ? stream : null);
  };
  return (
    <section className="panel space-y-5 p-5 sm:p-8" data-testid="iv-device-check">
      <h1 className="text-2xl font-semibold tracking-tight">Check your camera and microphone</h1>
      <p className="flex items-center gap-2 rounded-[6px] bg-brand-tint px-3 py-2 text-sm text-brand" data-testid="iv-record-notice"><Video size={14} aria-hidden="true" /> {RECORD_NOTICE}</p>
      <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
        <div className="relative aspect-video overflow-hidden rounded-[6px] bg-[#1B1E1A]">
          {cam === "ok" ? <video ref={ref} autoPlay muted playsInline className="h-full w-full -scale-x-100 object-cover" aria-label="Your camera preview" data-testid="iv-camera-preview" />
            : <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-sm text-white/80"><CameraOff size={22} aria-hidden="true" />{cam === "checking" ? "Asking for camera access…" : NO_CAMERA}</div>}
        </div>
        <div className="space-y-2">
          <p className="flex items-center gap-1 text-sm font-medium"><Mic size={14} aria-hidden="true" /> Microphone</p>
          <div className="h-2 overflow-hidden rounded-full bg-[#EFEBE3]" role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)} data-testid="iv-mic-level">
            <div className="h-full rounded-full bg-success transition-[width] duration-100" style={{ width: `${level * 100}%` }} />
          </div>
          <p className="text-xs text-ink-muted">Say something — the bar should move.</p>
        </div>
      </div>
      {cam === "unavailable" && <p role="status" className="rounded-[6px] bg-caution-tint px-3 py-2 text-sm text-caution" data-testid="iv-camera-unavailable">{NO_CAMERA}</p>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => go(true)} disabled={cam !== "ok"} className="h-11 px-5" data-testid="iv-continue-camera-btn"><Camera size={16} /> Continue with camera</Button>
        <Button variant="outline" onClick={() => go(false)} disabled={cam === "checking"} className="h-11 px-5" data-testid="iv-continue-voice-btn"><Mic size={16} /> Continue with voice only</Button>
      </div>
      <p className="text-xs text-ink-muted">Camera is recommended. Each answer's video is saved only for the hiring team to review.</p>
    </section>
  );
}
